import React, { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { FiUser, FiLock, FiCheck, FiAlertCircle } from "react-icons/fi";

const AdminProfile = () => {
  const { currentUser, updateUserProfile, changePassword } = useAuth();
  const [displayName, setDisplayName] = useState(currentUser?.displayName || "");
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passSaved, setPassSaved] = useState(false);
  const [passError, setPassError] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError(""); setProfileSaved(false); setProfileLoading(true);
    try {
      await updateUserProfile({ displayName });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      setProfileError("Failed to update profile.");
    }
    setProfileLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPassError(""); setPassSaved(false);
    if (newPass !== confirmPass) { setPassError("New passwords do not match."); return; }
    if (newPass.length < 6) { setPassError("Password must be at least 6 characters."); return; }
    setPassLoading(true);
    try {
      await changePassword(currentPass, newPass);
      setPassSaved(true);
      setCurrentPass(""); setNewPass(""); setConfirmPass("");
      setTimeout(() => setPassSaved(false), 3000);
    } catch (err) {
      if (err.code === "auth/wrong-password") setPassError("Current password is incorrect.");
      else setPassError("Failed to change password. Try again.");
    }
    setPassLoading(false);
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl">
        <div className="mb-8">
          <h1 className="text-2xl font-extrabold text-black">My Profile</h1>
          <p className="text-sm text-gray-400">Manage your admin account</p>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#e84fa5] rounded-full flex items-center justify-center">
              <FiUser size={28} className="text-white" />
            </div>
            <div>
              <p className="font-extrabold text-lg text-black">{currentUser?.displayName || "Admin"}</p>
              <p className="text-sm text-gray-400">{currentUser?.email}</p>
            </div>
          </div>

          <h2 className="text-[14px] font-extrabold mb-4 flex items-center gap-2">
            <FiUser size={16} className="text-[#e84fa5]" /> Update Display Name
          </h2>
          {profileError && <div className="bg-red-50 border border-red-200 text-red-600 text-[13px] px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><FiAlertCircle />{profileError}</div>}
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Display Name</label>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-[14px] outline-none focus:border-[#e84fa5] transition"
                placeholder="Your name" />
            </div>
            <div className="flex items-center gap-4">
              <button type="submit" disabled={profileLoading}
                className="px-6 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
                {profileLoading ? "Saving..." : "Update Name"}
              </button>
              {profileSaved && <span className="flex items-center gap-1 text-green-600 text-[13px] font-semibold"><FiCheck />Saved!</span>}
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-[14px] font-extrabold mb-4 flex items-center gap-2">
            <FiLock size={16} className="text-[#e84fa5]" /> Change Password
          </h2>
          {passError && <div className="bg-red-50 border border-red-200 text-red-600 text-[13px] px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><FiAlertCircle />{passError}</div>}
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {[
              { label: "Current Password", val: currentPass, set: setCurrentPass, ph: "Current password" },
              { label: "New Password", val: newPass, set: setNewPass, ph: "New password (min 6 chars)" },
              { label: "Confirm New Password", val: confirmPass, set: setConfirmPass, ph: "Confirm new password" },
            ].map((f) => (
              <div key={f.label}>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">{f.label}</label>
                <input type="password" value={f.val} onChange={(e) => f.set(e.target.value)} required
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-[14px] outline-none focus:border-[#e84fa5] transition"
                  placeholder={f.ph} />
              </div>
            ))}
            <div className="flex items-center gap-4">
              <button type="submit" disabled={passLoading}
                className="px-6 py-2.5 bg-gray-800 text-white font-bold text-[13px] rounded-lg hover:bg-gray-900 transition disabled:opacity-60">
                {passLoading ? "Changing..." : "Change Password"}
              </button>
              {passSaved && <span className="flex items-center gap-1 text-green-600 text-[13px] font-semibold"><FiCheck />Password changed!</span>}
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProfile;
